import { useEffect } from "react";
import * as Constants from "./util/canvas-chart-constants";
import {
    CanvasController,
    LineProperties,
    RectangleProperties,
    TextProperties,
} from "./util/canvas-controller";

export interface Split {
    label: string;
    start: number;
    end: number;
}

export interface CanvasChartProps {
    canvasId: string;
    categories: any[];
    barData: any[];
    parts: number;
    yLabels?: Map<number, string>;
    drawBarConnectionLines?: boolean;
    zoomLevel: number;
    splits?: Split[];
}

export function CanvasChart(props: CanvasChartProps) {
    useEffect(() => {
        render();
        const interval = setInterval(() => {
            render();
        }, Constants.RENDER_INTERVAL);
        return () => {
            clearInterval(interval);
        };
    });

    const render = () => {
        const canvasController = new CanvasController(
            `canvas-chart-${props.canvasId}`
        );

        if (!canvasController.getCanvas()) return;

        canvasController.getCanvas().style.width =
            Constants.CHART_WIDTH * props.zoomLevel + "%";
        canvasController.getCanvas().style.height =
            Constants.SPLIT_HEIGHT +
            Constants.CATEGORY.ICON.HEIGHT * props.categories.length +
            Constants.BOTTOM_LABEL.HEIGHT +
            "px";

        canvasController.getCanvas().width =
            canvasController.getCanvas().offsetWidth;
        canvasController.getCanvas().height =
            canvasController.getCanvas().offsetHeight;

        clearCanvas(canvasController);
        drawSplits(canvasController);
        drawBars(canvasController);
        drawYLabels(canvasController);
        drawGreyZone(canvasController);
        drawGraphBorder(canvasController);
        drawCategorySeparationLines(canvasController);
    };

    const drawGraphBorder = (canvasController: CanvasController) => {
        const top = Constants.SPLIT_HEIGHT;
        const left = 0;

        const width = canvasController.getWidth();

        const height =
            canvasController.getHeight() -
            Constants.BOTTOM_LABEL.HEIGHT -
            Constants.SPLIT_HEIGHT;

        const rectProp: RectangleProperties = {
            borderColor: Constants.CATEGORY.SEPARATION_LINE.COLOR,
            color: "#22B3B30A",
        };

        canvasController.drawRoundedRectangle(
            left,
            top,
            width,
            height,
            6,
            rectProp
        );
    };

    const drawSplits = (canvasController: CanvasController) => {
        if (props.splits) {
            const contentWidth =
                canvasController.getWidth() - Constants.INNER_PADDING * 2;
            const splitWidth = contentWidth / props.splits.length;
            const textY = Constants.SPLIT_TEXT_TOP;

            props.splits.forEach((split: Split, index: number) => {
                const splitStart =
                    (split.start / props.parts) * contentWidth +
                    Constants.INNER_PADDING;

                const splitEnd =
                    (split.end / props.parts) * contentWidth +
                    Constants.INNER_PADDING;

                const textProps: TextProperties = {
                    color: Constants.SPLIT.TEXT.COLOR,
                    font: Constants.BARS.TEXT.FONT,
                    fontSize: Constants.SPLIT.TEXT.FONT_SIZE,
                    textAlign: Constants.BARS.TEXT.ALIGN,
                    textBaseline: Constants.BARS.TEXT.BASE_LINE,
                };

                canvasController.drawText(
                    split.label,
                    splitStart + (splitEnd - splitStart) / 2,
                    textY,
                    textProps,
                    splitWidth
                );

                const topPoint = 0;
                const lineProps = {
                    width: Constants.CATEGORY.SEPARATION_LINE.SIZE,
                    color: Constants.CATEGORY.SEPARATION_LINE.COLOR,
                };

                canvasController.drawLine(
                    splitStart,
                    topPoint,
                    splitStart,
                    canvasController.getHeight() -
                        Constants.BOTTOM_LABEL.HEIGHT,
                    lineProps
                );
            });
        }
    };

    const drawGreyZone = (canvasController: CanvasController) => {
        let leftLimit = props.parts;

        props.barData.forEach((bar) => {
            if (bar.end - bar.start > 0)
                if (bar.start < leftLimit) leftLimit = bar.start;
        });

        if (leftLimit > 0) {
            const contentWidth =
                canvasController.getWidth() - Constants.INNER_PADDING * 2;

            let right =
                (leftLimit / props.parts) * contentWidth +
                Constants.INNER_PADDING;

            const rectProps: RectangleProperties = {
                color: Constants.BARS.NO_ACTIVITY_ZONE_COLOR,
            };

            if (leftLimit === props.parts) {
                right = canvasController.getWidth();
            }

            canvasController.drawRectangle(
                0,
                Constants.SPLIT_HEIGHT,
                right,
                Constants.CATEGORY.ICON.HEIGHT * props.categories.length,
                rectProps
            );
        }
    };

    const clearCanvas = (canvasController: CanvasController) => {
        canvasController.clearCanvas(Constants.BARS.BACKGROUND_COLOR);
    };

    const drawCategorySeparationLines = (
        canvasController: CanvasController
    ) => {
        const categoryHeight = Constants.CATEGORY.ICON.HEIGHT;
        const width = canvasController.getWidth();

        props.categories.forEach((_category, index) => {
            const topPoint =
                index * categoryHeight +
                categoryHeight +
                Constants.SPLIT_HEIGHT -
                0.5;

            const lineProps: LineProperties = {
                color: Constants.CATEGORY.SEPARATION_LINE.COLOR,
                width: Constants.CATEGORY.SEPARATION_LINE.SIZE,
            };

            canvasController.drawLine(0, topPoint, width, topPoint, lineProps);
        });
    };

    const drawYLabels = (canvasController: CanvasController) => {
        const labelMap = props.yLabels;

        const labelTop =
            canvasController.getHeight() -
            Constants.BOTTOM_LABEL.TEXT.BOTTOM_PADDING;
        const contentWidth =
            canvasController.getWidth() - Constants.INNER_PADDING * 2;

        labelMap?.forEach((val, key) => {
            const labelLeft =
                (key / props.parts) * contentWidth + Constants.INNER_PADDING;

            const textProps: TextProperties = {
                color: Constants.BOTTOM_LABEL.TEXT.COLOR,
                font: Constants.BOTTOM_LABEL.TEXT.FONT,
                fontSize: Constants.BOTTOM_LABEL.TEXT.FONT_SIZE,
                textAlign: Constants.BOTTOM_LABEL.TEXT.ALIGN,
                textBaseline: Constants.BOTTOM_LABEL.TEXT.BASE_LINE,
            };

            canvasController.drawText(val, labelLeft, labelTop, textProps);
        });
    };

    const drawBars = (canvasController: CanvasController) => {
        props.barData.forEach((barData) => {
            const contentWidth =
                canvasController.getWidth() - Constants.INNER_PADDING * 2;
            const categoryHeight = Constants.CATEGORY.ICON.HEIGHT;

            const barHeight = Math.round(
                categoryHeight * Constants.BARS.BAR_HEIGHT_PERCENTAGE
            );
            const barWidth =
                ((barData.end - barData.start) / props.parts) * contentWidth;

            const barLeft =
                (barData.start / props.parts) * contentWidth +
                Constants.INNER_PADDING;
            const barTop =
                Math.round(barData.category * categoryHeight) +
                categoryHeight / 2 -
                barHeight / 2 +
                Constants.SPLIT_HEIGHT;

            const textProp: TextProperties = {
                color: Constants.BARS.TEXT.COLOR,
                font: Constants.BARS.TEXT.FONT,
                fontSize: Constants.BARS.TEXT.FONT_SIZE,
                textAlign: Constants.BARS.TEXT.ALIGN,
                textBaseline: Constants.BARS.TEXT.BASE_LINE,
            };

            const rectProp: RectangleProperties = {
                color: props.categories[barData.category]
                    ? props.categories[barData.category].color
                    : "black",
            };

            canvasController.drawRectangle(
                barLeft,
                barTop,
                barWidth,
                barHeight,
                rectProp,
                barData.label,
                textProp
            );
        });
    };

    return <canvas id={`canvas-chart-${props.canvasId}`} />;
}
